<template>
  <div v-if="error" class="alert alert-danger">
    <h5 class="alert-heading">{{ $t("js.backend.voter_importer.error") }}</h5>
    <details>
      <summary v-text="error.message"/>
      <pre v-text="error.backtrace.join('\n')"/>
    </details>
  </div>
  <div v-else-if="doneAt">
    {{ $t("js.backend.components.contest_importer.done") }}
  </div>
  <div v-else>
    <p>
      <time :datetime="startedAt" v-if="startedAt">{{ $t("js.backend.voter_importer.started_time_ago", {time_ago: startedAgo}) }}</time>
      <span v-else>{{ $t("js.backend.voter_importer.starting_soon") }}</span>
    </p>
    <div class="progress mb-5">
        <div class="progress-bar" role="progressbar" :aria-valuenow="progressCurrent" aria-valuemin="0" :aria-valuemax="progressTotal" :style="{width: progressPercentage}">
          {{ `${progressCurrent}/${progressTotal}` }}
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { ContestImporterChannelSubscription } from "@/channels/ContestImporterChannel";
import i18n from "@/entrypoints/shared/i18n_backend";
import type { ContestImporter, ContestImporterInitialData, PropType, Locale } from "@/types";

const relativeTimeFormatter = new Intl.RelativeTimeFormat((i18n.global.locale || "da") as Locale, { numeric: "auto" });

export default defineComponent({
  name: "ContestImporterProgressBar",
  props: {
    importerId: {
      type: Number,
      required: true,
    },
    initialProgress: {
      type: Object as PropType<ContestImporterInitialData>,
      required: true,
    },
    initialWorkflowState: {
      type: String,
      required: true,
    },
  },
  data({ initialProgress }) {
    return {
      progressCurrent: initialProgress.current,
      progressTotal: initialProgress.total,
      reloading: false,
      intervalId: null,
      startedAgo: null,
      error: null,
      subscription: null,
      startedAt: null,
      doneAt: null,
    }
  },
  computed: {
    progressPercentage() {
      return `${Math.round(100 * this.progressCurrent / this.progressTotal)}%`;
    },
    startedAtDateTime() {
      return Date.parse(this.startedAt);
    },
  },
  beforeMount() {
    this.subscription = new ContestImporterChannelSubscription(this.importerId, {
      received: this.received
    });
  },
  mounted() {
    this.updateTimestamp();
    this.intervalId = setInterval(this.updateTimestamp, 1000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
  methods: {
    updateTimestamp() {
      if (!this.startedAtDateTime) return;
      const value = Math.round((this.startedAtDateTime-Date.now()) / 1000);
      if (Math.abs(value) >= 60) {
        this.startedAgo = relativeTimeFormatter.format(Math.floor(value / 60), "minute");
      } else {
        this.startedAgo = relativeTimeFormatter.format(value, "second");
      }
    },
    received(data: ContestImporter) {
      const { progress_current, progress_total, workflow_state, job_started_at, job_done_at, job_last_error } = data;
      this.progressCurrent = progress_current;
      this.progressTotal = progress_total;
      this.startedAt = job_started_at;
      this.doneAt = job_done_at;
      this.error = job_last_error;
      if (this.initialWorkflowState !== workflow_state) this.reloadPage();
    },
    reloadPage() {
      if (this.reloading) return;
      this.reloading = true;
      setTimeout(() => window.location.reload(), 500); // Small delay, so the user can see the progressbar hit 100%
    },
  },
});
</script>
